// Oireachtas syllabus builder (SyllabusFies.jsx) row mappings, kept pure so
// the load -> save round trip is tested: rowFromComp turns a stored or template
// competition into an editor row, compFromRow turns an editor row back into
// the PUT /oireachtas/:id/competitions body. Every catalog field the tab does
// not edit must pass through both — a re-save that dropped one silently
// rewrote the catalog (role/linkedTo/premiereConfig/... broke teacher
// registration; roundDanceOptions/roundDanceRound dropped the Adult and Under 7
// round-dance choices).

export const typeForLevel = (level, code = '') => {
  const g = (level || '').toLowerCase();
  if (g.includes('treble')) return 'treble_reel';
  if (g.includes('premiere')) return 'premiere';
  if (g.includes('team')) {
    if (/^T2/i.test(code)) return 'team_2h';
    if (/^T3/i.test(code)) return 'team_3h';
    if (/^T6/i.test(code)) return 'team_6h';
    if (/^T8/i.test(code)) return 'team_8h';
    return 'team_4h';
  }
  return 'solo_champ';
};

// Cascade role for rows built by hand in this tab (no stored role): the
// registration UI ONLY offers competitions with a role, so a role-less row
// is priced-but-unregisterable. Stored values always win.
export const roleForType = (type) => {
  if (['solo_champ', 'premiere', 'trad_set', 'adult_solo', 'advanced_adult'].includes(type)) return 'primary';
  if (type === 'treble_reel') return 'addon';
  return 'standalone';
};
export const linkedToFor = (type, group = '') =>
  type === 'treble_reel' ? (/premiere/i.test(group) ? 'premiere' : 'solo_champ') : undefined;

// Stored/template competition -> editor row. agesCode22: the age-chip tokens.
export const rowFromComp = (c, agesCode22 = []) => ({
  code: c.code || '', description: c.description || c.name || '', price: c.price ?? '',
  // Fall back to ageGroup only when it IS a chip token — label-style
  // ageGroups ('Adult', 'Open', '15 & Over') would otherwise become
  // phantom, un-deselectable chips that persist on resave (e.g. DD).
  ages: c.ages?.length ? c.ages : (agesCode22.includes(c.ageGroup) ? [c.ageGroup] : []),
  // Preserve unset values — coercing (gender||'girls', adj||5, rounds||3)
  // meant an untouched load->resave silently rewrote the catalog.
  gender: c.gender || '', adjudicators: c.adjudicators ?? '', rounds: c.rounds ?? '',
  dropHighLow: !!c.dropHighLow, qualifier: !!c.qualifier,
  // Cascade catalog fields (entry-cascade.md): not editable in this tab
  // yet, but they MUST round-trip — a re-save that stripped role/
  // linkedTo/premiereConfig/teamConfig/bornYear broke teacher registration.
  type: c.type, role: c.role, linkedTo: c.linkedTo,
  premiereConfig: c.premiereConfig, teamConfig: c.teamConfig,
  bornYear: c.bornYear, day: c.day, setDanceGate: c.setDanceGate,
  setDanceOptions: c.setDanceOptions, ageGroup: c.ageGroup,
  // Round-dance choice (Adult round 2, Under 7 round 3 recall).
  roundDanceOptions: c.roundDanceOptions, roundDanceRound: c.roundDanceRound,
});

// Editor row (+ its level title) -> competition for the PUT body.
export const compFromRow = (c, groupTitle) => {
  // Prefer the STORED type (seeded/template catalogs are authoritative);
  // the title heuristic only classifies rows typed fresh into this tab.
  const type = c.type || typeForLevel(groupTitle, c.code);
  const role = c.role || roleForType(type, groupTitle);
  const linkedTo = c.linkedTo || (role === 'addon' ? linkedToFor(type, groupTitle) : undefined);
  return {
    group: groupTitle, code: c.code, name: c.description, description: c.description,
    type, role, price: Number(c.price) || 0, ages: c.ages,
    // Preserve the stored label ('Adult', 'Under 13') — deriving from
    // ages[0] rewrote every seeded ageGroup on an untouched load->Next.
    ageGroup: c.ageGroup || c.ages[0] || '',
    // Unset catalog values stay unset — do NOT persist UI display defaults.
    ...(c.gender && { gender: c.gender }),
    ...(c.adjudicators !== '' && c.adjudicators != null && { adjudicators: Number(c.adjudicators) }),
    ...(c.rounds !== '' && c.rounds != null && { rounds: Number(c.rounds) }),
    dropHighLow: c.dropHighLow, qualifier: c.qualifier,
    ...(linkedTo !== undefined && { linkedTo }),
    ...(c.premiereConfig !== undefined && { premiereConfig: c.premiereConfig }),
    ...(c.teamConfig !== undefined && { teamConfig: c.teamConfig }),
    ...(c.bornYear !== undefined && { bornYear: c.bornYear }),
    ...(c.day !== undefined && { day: c.day }),
    ...(c.setDanceGate !== undefined && { setDanceGate: c.setDanceGate }),
    ...(c.setDanceOptions !== undefined && { setDanceOptions: c.setDanceOptions }),
    ...(c.roundDanceOptions !== undefined && { roundDanceOptions: c.roundDanceOptions }),
    ...(c.roundDanceRound !== undefined && c.roundDanceRound !== null && { roundDanceRound: c.roundDanceRound }),
  };
};
